import {
  COVER_DEFAULTS,
  type CoverStyle,
  type CoverTextMode,
  EVENT_FLOW_PRICES,
  type EventChannel,
  type EventFlowState,
  eventFlowFormatsPrice,
  eventFlowStepPrice,
  type LogoPlacement,
} from '@pulsify/shared/dto'

export type AssistantKind =
  | 'cover-choice'
  | 'style'
  | 'logo'
  | 'text'
  | 'channels'
  | 'variants'
  | 'refined'
  | 'cover-done'
  | 'photo'
  | 'photo-done'
  | 'description'
  | 'features'
  | 'preview'
  | 'when'
  | 'place'
  | 'entry'
  | 'category'
  | 'review'
  | 'noted'
  | 'failed'

type FlowTurn =
  | { id: number; role: 'user'; text: string }
  | { id: number; role: 'assistant'; kind: AssistantKind }

interface CoverChoices {
  style: CoverStyle
  logo: LogoPlacement
  logoLarge: boolean
  text: CoverTextMode
  channels: EventChannel[]
}

export interface FlowMachine {
  turns: FlowTurn[]
  nextId: number
  spent: number
  flow: EventFlowState | null
  cover: CoverChoices
  note: string
}

type FlowAction =
  | { type: 'drafted'; text: string; cost: number; flow: EventFlowState | null }
  | { type: 'cover-started'; flow: EventFlowState }
  | { type: 'reset' }
  | { type: 'answer'; text: string; next: AssistantKind; patch?: Partial<CoverChoices> }
  | { type: 'say'; kind: AssistantKind }
  | { type: 'charged'; cost: number; flow?: EventFlowState }
  | { type: 'choose'; patch: Partial<CoverChoices> }
  | { type: 'note'; text: string }

export const initialFlow: FlowMachine = {
  turns: [],
  nextId: 1,
  spent: 0,
  flow: null,
  cover: { ...COVER_DEFAULTS, logoLarge: false, channels: ['pulsify-cover'] },
  note: '',
}

type NewTurn = { role: 'user'; text: string } | { role: 'assistant'; kind: AssistantKind }

function withTurns(state: FlowMachine, ...turns: NewTurn[]): FlowMachine {
  let nextId = state.nextId
  const added = turns.map((turn) => ({ ...turn, id: nextId++ }) as FlowTurn)
  return { ...state, turns: [...state.turns, ...added], nextId }
}

/** The pulsify cover is always made; every other channel is the organizer's choice. */
function withCoverChannel(channels: EventChannel[]): EventChannel[] {
  return ['pulsify-cover', ...channels.filter((channel) => channel !== 'pulsify-cover')]
}

export function flowReducer(state: FlowMachine, action: FlowAction): FlowMachine {
  switch (action.type) {
    case 'drafted':
      return withTurns(
        { ...state, spent: state.spent + action.cost, flow: action.flow },
        { role: 'user', text: action.text },
        { role: 'assistant', kind: 'cover-choice' },
      )
    case 'cover-started':
      return withTurns(
        { ...initialFlow, flow: action.flow },
        { role: 'assistant', kind: 'cover-choice' },
      )
    case 'reset':
      return initialFlow
    case 'answer': {
      const cover = action.patch ? { ...state.cover, ...action.patch } : state.cover
      return withTurns(
        { ...state, cover: { ...cover, channels: withCoverChannel(cover.channels) } },
        { role: 'user', text: action.text },
        { role: 'assistant', kind: action.next },
      )
    }
    case 'say':
      return withTurns(state, { role: 'assistant', kind: action.kind })
    case 'charged':
      return { ...state, spent: state.spent + action.cost, flow: action.flow ?? state.flow }
    case 'choose': {
      const cover = { ...state.cover, ...action.patch }
      return { ...state, cover: { ...cover, channels: withCoverChannel(cover.channels) } }
    }
    case 'note':
      return withTurns(
        { ...state, note: [state.note, action.text].filter(Boolean).join(' ').slice(0, 300) },
        { role: 'user', text: action.text },
        { role: 'assistant', kind: 'noted' },
      )
  }
}

/** The last assistant turn, the only one whose questions can still be answered. */
export function activeTurnId(state: FlowMachine): number | null {
  for (let i = state.turns.length - 1; i >= 0; i--) {
    const turn = state.turns[i]
    if (turn.role === 'assistant' && turn.kind !== 'noted') return turn.id
  }
  return null
}

/** What making the variants costs now: nothing while the package still covers the cover. */
export function coverPrice(flow: EventFlowState | null): number {
  return eventFlowStepPrice('cover', flow)
}

/** Every channel beyond the pulsify cover is recomposed for a price, minus the package's free ones. */
export function extraChannelsPrice(channels: EventChannel[], flow: EventFlowState | null): number {
  const extra = channels.filter((channel) => channel !== 'pulsify-cover').length
  return eventFlowFormatsPrice(extra, flow?.free.formats ?? 0)
}

/** Finishing a chosen variant is included once per batch, every further one is paid. */
export function finalizePrice(flow: EventFlowState | null): number {
  return flow && flow.free.finalize > 0 ? 0 : EVENT_FLOW_PRICES.refine
}

export function translationPrice(flow: EventFlowState | null): number {
  return eventFlowStepPrice('translation', flow)
}
